import { useState, useEffect } from "react";
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  useUpdateClinicMutation,
  type Clinic,
  type ClinicRequest,
} from "@/api/clinicApi";
import { useListTestsQuery } from "@/api/testApi";
import { useGetVendorByIdQuery } from "@/api/vendorApi";
import { useListDrugPanelsQuery } from "@/features/drug-panels/services/drugPanelApi";
import ClinicTestsMultiSelect from "@/features/clinics/components/ClinicTestsMultiSelect";
import DrugPanelMultiSelect from "@/features/drug-panels/components/DrugPanelMultiSelect";
import { 
  Building2, 
  FlaskConical, 
  DollarSign, 
  Clock,
  AlertCircle
} from "lucide-react";
import { toast } from "sonner";

interface EditClinicProps {
  clinic: Clinic;
  onClose: () => void;
}

interface OperatingHours {
  mon: string;
  tue: string;
  wed: string;
  thu: string;
  fri: string;
  sat: string;
  sun: string;
  [key: string]: string;
}

export default function EditClinic({ clinic, onClose }: EditClinicProps) {
  const [activeTab, setActiveTab] = useState("general");
  const { data: allTestTypes = [] } = useListTestsQuery();
  const { data: vendor } = useGetVendorByIdQuery(clinic.vendorId);
  const { data: drugPanels = [] } = useListDrugPanelsQuery({});

  // Filter out drug panels (stored as test types with category "Substance Abuse Testing")
  const availableTests = allTestTypes.filter(test => test.testCategory !== "Substance Abuse Testing");

  const parsedDrugAvailability = clinic.drugPanelAvailability ? JSON.parse(clinic.drugPanelAvailability) : [];
  const parsedPricing = clinic.clinicLevelPricing ? JSON.parse(clinic.clinicLevelPricing) : { tests: {}, drugs: {} };
  const initialOperatingHours = clinic.operatingHours ? JSON.parse(clinic.operatingHours) : null;

  const [formData, setFormData] = useState<ClinicRequest>({
    clinicName: clinic.clinicName || "",
    clinicAddress: clinic.clinicAddress || "",
    city: clinic.city || "",
    state: clinic.state || "",
    zipCode: clinic.zipCode || "",
    contactPhone: clinic.contactPhone || "",
    status: clinic.status || "ACTIVE",
    testTypeIds: clinic.testTypeIds || [],
    appointmentRequired: clinic.appointmentRequired ?? false,
    cocRequired: clinic.cocRequired ?? false,
    collectionFee: clinic.collectionFee || undefined,
    drugPanelAvailability: clinic.drugPanelAvailability || undefined,
    clinicLevelPricing: clinic.clinicLevelPricing || undefined,
    resultDeliveryOverride: clinic.resultDeliveryOverride ? clinic.resultDeliveryOverride : "DEFAULT",
  });

  const [selectedDrugPanelIds, setSelectedDrugPanelIds] = useState<number[]>(
    Array.isArray(parsedDrugAvailability) ? parsedDrugAvailability : []
  );
  const [testPrices, setTestPrices] = useState<Record<number, string>>(parsedPricing.tests || {});
  const [drugPrices, setDrugPrices] = useState<Record<number, string>>(parsedPricing.drugs || {});
  const [panelAliases, setPanelAliases] = useState<Record<number, string>>(
    clinic.panelAliasMapping ? JSON.parse(clinic.panelAliasMapping) : {}
  );
  const [showOperatingHours, setShowOperatingHours] = useState(!!initialOperatingHours);
  const [operatingHours, setOperatingHours] = useState<OperatingHours>(
    initialOperatingHours || {
      mon: "09:00-17:00",
      tue: "09:00-17:00",
      wed: "09:00-17:00",
      thu: "09:00-17:00",
      fri: "09:00-17:00",
      sat: "closed",
      sun: "closed",
    }
  );

  const [hasCollectionFee, setHasCollectionFee] = useState<boolean>(
    clinic.collectionFee !== undefined && clinic.collectionFee !== null && clinic.collectionFee > 0
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [updateClinic, { isLoading }] = useUpdateClinicMutation();

  const isVariesByClinic = (value?: string) =>
    (value || "").toUpperCase() === "VARIES_BY_CLINIC";

  const resolveRequirementValue = (requirement?: string, currentValue?: boolean) => {
    const normalized = (requirement || "").toUpperCase();
    if (normalized === "REQUIRED_ALL") return true;
    if (normalized === "NOT_REQUIRED") return false;
    return currentValue ?? false;
  };


  useEffect(() => {
    if (vendor) {
      if (vendor.uniformOperatingHours) {
        setShowOperatingHours(false);
        try {
          const parsed = JSON.parse(vendor.uniformOperatingHours);
          setOperatingHours(parsed);
        } catch (e) {
          // Silent fail
        }
      }

      if (vendor.uniformTestConfiguration) {
        try {
          const parsedConfig = JSON.parse(vendor.uniformTestConfiguration);
          let testIds: number[] = [];
          if (Array.isArray(parsedConfig)) {
            if (parsedConfig.length > 0 && typeof parsedConfig[0] === 'object') {
              testIds = parsedConfig.map((item: any) => item.testId);
            } else {
              testIds = parsedConfig;
            }
          }
          setFormData(prev => ({ ...prev, testTypeIds: testIds }));
        } catch (e) {
          // Silent fail
        }
      }

      if (vendor.uniformDrugConfiguration) {
        try {
          const parsedDrugConfig = JSON.parse(vendor.uniformDrugConfiguration);
          if (Array.isArray(parsedDrugConfig)) {
            const ids = parsedDrugConfig.map((item: any) => item.drugPanelId);
            setSelectedDrugPanelIds(ids);
          }
        } catch (e) {
          // Silent fail
        }
      }

      setFormData((prev) => ({
        ...prev,
        appointmentRequired: resolveRequirementValue(
          vendor.appointmentRequirement,
          prev.appointmentRequired
        ),
        cocRequired: resolveRequirementValue(
          vendor.cocRequirement,
          prev.cocRequired
        ),
      }));
    }
  }, [vendor]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleTestTypesChange = (selectedIds: number[]) => {
    setFormData((prev) => ({ ...prev, testTypeIds: selectedIds }));
    const newPrices = { ...testPrices };
    Object.keys(newPrices).forEach(id => {
      if (!selectedIds.includes(parseInt(id))) delete newPrices[parseInt(id)];
    });
    setTestPrices(newPrices);

    if (errors.testTypeIds) {
      setErrors((prev) => ({ ...prev, testTypeIds: "" }));
    }
  };

  const handleDrugPanelsChange = (selectedIds: number[]) => {
    setSelectedDrugPanelIds(selectedIds);
    const newPrices = { ...drugPrices };
    Object.keys(newPrices).forEach(id => {
      if (!selectedIds.includes(parseInt(id))) delete newPrices[parseInt(id)];
    });
    setDrugPrices(newPrices);

    const newAliases = { ...panelAliases };
    Object.keys(newAliases).forEach(id => {
      if (!selectedIds.includes(parseInt(id))) delete newAliases[parseInt(id)];
    });
    setPanelAliases(newAliases);
  };

  const handlePanelAliasChange = (panelId: number, alias: string) => {
    setPanelAliases(prev => ({ ...prev, [panelId]: alias }));
  };

  const handleTestPriceChange = (testId: number, price: string) => {
    setTestPrices({ ...testPrices, [testId]: price });
    // Clear error when user starts typing
    if (errors[`testPrice-${testId}`]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[`testPrice-${testId}`];
        if (Object.keys(newErrors).filter(k => k.startsWith('testPrice-')).length === 0) {
          delete newErrors.testPricesSummary;
        }
        return newErrors;
      });
    }
  };

  const handleDrugPriceChange = (panelId: number, price: string) => {
    setDrugPrices({ ...drugPrices, [panelId]: price });
    // Clear error when user starts typing
    if (errors[`drugPrice-${panelId}`]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[`drugPrice-${panelId}`];
        if (Object.keys(newErrors).filter(k => k.startsWith('drugPrice-')).length === 0) {
          delete newErrors.drugPricesSummary;
        }
        return newErrors;
      });
    }
  };


  const handleOperatingHoursChange = (day: keyof OperatingHours, value: string) => {
    setOperatingHours({ ...operatingHours, [day]: value });
  };

  const shouldShowOperationsTab = (): boolean => {
    // Show operations tab if vendor has location-specific collection fee configuration
    if (vendor?.collectionFeeConfiguration) {
      try {
        const config = typeof vendor.collectionFeeConfiguration === 'string'
          ? JSON.parse(vendor.collectionFeeConfiguration)
          : vendor.collectionFeeConfiguration;
        return config?.type === "LOCATION_SPECIFIC";
      } catch {
        return false;
      }
    }
    return false;
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Basic required fields
    if (!formData.clinicName.trim()) {
      newErrors.clinicName = "Clinic name is required";
    }

    if (!formData.city.trim()) {
      newErrors.city = "City is required";
    }

    if (!formData.state.trim()) {
      newErrors.state = "State is required";
    }

    if (!formData.contactPhone.trim()) {
      newErrors.contactPhone = "Contact phone is required";
    } else if (!/^\+?[0-9\s\-()]{10,}$/.test(formData.contactPhone)) {
      newErrors.contactPhone = "Invalid phone number format (minimum 10 digits)";
    }

    if (!formData.testTypeIds || formData.testTypeIds.length === 0) {
      newErrors.testTypeIds = "Please select at least one test type";
    }

    // Validate test prices when clinic-level pricing is required
    if (formData.testTypeIds && formData.testTypeIds.length > 0 && vendor) {
      if (vendor.pricingModel === "LOCATION_SPECIFIC") {
        const missingTestPrices = formData.testTypeIds.filter(testId => {
          const price = testPrices[testId];
          const isValid = !price || price.trim() === "" || isNaN(parseFloat(price)) || parseFloat(price) <= 0;
          if (isValid) newErrors[`testPrice-${testId}`] = `Price for test #${testId} is required and must be greater than 0`;
          return isValid;
        });

        if (missingTestPrices.length > 0) {
          newErrors.testPricesSummary = `Please provide valid prices for all selected tests. Missing prices for ${missingTestPrices.length} test${missingTestPrices.length !== 1 ? 's' : ''}.`;
        }
      }
    }

    // Validate drug panel prices when clinic-level drug pricing is required
    if (selectedDrugPanelIds.length > 0 && vendor) {
      if (!vendor.uniformDrugConfiguration) {
        const missingDrugPrices = selectedDrugPanelIds.filter(panelId => {
          const price = drugPrices[panelId];
          const isValid = !price || price.trim() === "" || isNaN(parseFloat(price)) || parseFloat(price) <= 0;
          if (isValid) newErrors[`drugPrice-${panelId}`] = `Price for drug panel #${panelId} is required and must be greater than 0`;
          return isValid;
        });

        if (missingDrugPrices.length > 0) {
          newErrors.drugPricesSummary = `Please provide valid prices for all selected drug panels. Missing prices for ${missingDrugPrices.length} panel${missingDrugPrices.length !== 1 ? 's' : ''}.`;
        }
      }
    }


    // Validate operating hours if clinic-specific hours are enabled
    if (!vendor?.uniformOperatingHours && showOperatingHours) {
      const requiredDays: (keyof OperatingHours)[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
      const missingHours = requiredDays.filter(day => !operatingHours[day] || operatingHours[day].trim() === "");

      if (missingHours.length > 0) {
        newErrors.operatingHours = `Please provide operating hours for all days. Missing hours for: ${missingHours.join(', ')}`;
      }
    }

    // Validate collection fee if location-specific is required
    if (vendor?.collectionFeeConfiguration) {
      try {
        const config = typeof vendor.collectionFeeConfiguration === 'string'
          ? JSON.parse(vendor.collectionFeeConfiguration)
          : vendor.collectionFeeConfiguration;

        if (config?.type === "LOCATION_SPECIFIC") {
          if (hasCollectionFee && (formData.collectionFee === undefined || formData.collectionFee === null ||
              formData.collectionFee.toString().trim() === "" || isNaN(parseFloat(formData.collectionFee.toString())) ||
              parseFloat(formData.collectionFee.toString()) < 0)) {
            newErrors.collectionFee = "Collection fee is required and must be non-negative for location-specific configuration.";
          }
        }
      } catch (e) {
        console.error("Error parsing vendor collectionFeeConfiguration in validation:", e);
      }
    }

    // Validate result delivery override
    if (formData.resultDeliveryOverride && !["DEFAULT", "EMAIL", "FAX", "VENDOR_PORTAL"].includes(formData.resultDeliveryOverride)) {
      newErrors.resultDeliveryOverride = "Invalid result delivery method";
    }

    console.log("EditClinic validation errors:", newErrors);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await updateClinic({
        id: clinic.id,
        data: {
          ...formData,
          resultDeliveryOverride: formData.resultDeliveryOverride === "DEFAULT" ? undefined : formData.resultDeliveryOverride,
          operatingHours: vendor?.uniformOperatingHours 
              ? vendor.uniformOperatingHours 
              : (showOperatingHours ? JSON.stringify(operatingHours) : undefined),
          drugPanelAvailability: vendor?.uniformDrugConfiguration
              ? undefined
              : JSON.stringify(selectedDrugPanelIds),
          panelAliasMapping: vendor?.uniformDrugConfiguration
              ? undefined
              : JSON.stringify(panelAliases),
          clinicLevelPricing: JSON.stringify({
            tests: vendor?.pricingModel === "UNIFORM" ? undefined : testPrices,
            drugs: vendor?.uniformDrugConfiguration ? undefined : drugPrices
          }),
        },
      }).unwrap();

      toast.success("Clinic updated successfully");
      setErrors({});
      onClose();
    } catch (error: any) {
      toast.error(error?.data?.message || "Failed to update clinic");
    }
  };

  return (
    <div className="flex flex-col h-[85vh]">
      <div className="p-6 border-b bg-muted/20">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className={`grid w-full max-w-2xl mx-auto ${
            vendor?.pricingModel === "LOCATION_SPECIFIC" || shouldShowOperationsTab()
              ? "grid-cols-4"
              : "grid-cols-2"
          }`}>
            <TabsTrigger value="general" className="gap-2">
              <Building2 className="h-4 w-4" /> <span className="hidden sm:inline">General</span>
            </TabsTrigger>
            <TabsTrigger value="services" className="gap-2">
              <FlaskConical className="h-4 w-4" /> <span className="hidden sm:inline">Services</span>
            </TabsTrigger>
            {vendor?.pricingModel === "LOCATION_SPECIFIC" && (
              <TabsTrigger value="pricing" className="gap-2">
                <DollarSign className="h-4 w-4" /> <span className="hidden sm:inline">Pricing</span>
              </TabsTrigger>
            )}
            {shouldShowOperationsTab() && (
              <TabsTrigger value="operations" className="gap-2">
                <Clock className="h-4 w-4" /> <span className="hidden sm:inline">Operations</span>
              </TabsTrigger>
            )}
          </TabsList>
        </Tabs>
      </div>

      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
        <Tabs value={activeTab} className="w-full">
          {/* General Tab */}
          <TabsContent value="general" className="p-6 space-y-6 m-0 outline-none">
            <Card className="border-none shadow-none bg-transparent">
              <CardHeader className="px-0">
                <CardTitle className="text-xl flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-primary" /> Basic Information
                </CardTitle>
                <CardDescription>Primary identification and contact details for the clinic.</CardDescription>
              </CardHeader>
              <CardContent className="px-0 space-y-4">
                {/* Vendor Info Display */}
                {vendor && (
                  <div className="space-y-3">
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-semibold text-blue-900">Vendor:</span>
                        <span className="text-blue-800">{vendor.vendorName}</span>
                      </div>
                      <div className="text-xs text-gray-600 space-y-1">
                        <p>Contact: {vendor.contactEmail} | {vendor.contactPhone}</p>
                        <p>Location: {vendor.city}, {vendor.state}</p>
                      </div>
                    </div>
                    
                    {/* Vendor Configuration Summary */}
                    <div className="p-3 bg-indigo-50 border border-indigo-200 rounded">
                      <h4 className="text-sm font-semibold text-indigo-900 mb-2">Vendor Configuration Summary</h4>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="font-medium">Pricing Model:</span>{" "}
                          <span className={vendor.pricingModel === "UNIFORM" ? "text-orange-600 font-semibold" : ""}>
                            {vendor.pricingModel || "LOCATION_SPECIFIC"}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium">Operating Hours:</span>{" "}
                          <span className={vendor.uniformOperatingHours ? "text-orange-600 font-semibold" : "text-green-600"}>
                            {vendor.uniformOperatingHours ? "UNIFORM" : "Location-Specific"}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium">Test Configuration:</span>{" "}
                          <span className={vendor.uniformTestConfiguration ? "text-orange-600 font-semibold" : "text-green-600"}>
                            {vendor.uniformTestConfiguration ? "UNIFORM" : "Location-Specific"}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium">Drug Configuration:</span>{" "}
                          <span className={vendor.uniformDrugConfiguration ? "text-orange-600 font-semibold" : "text-green-600"}>
                            {vendor.uniformDrugConfiguration ? "UNIFORM" : "Location-Specific"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <Label htmlFor="clinicName">Clinic Name *</Label>
                  <Input
                    id="clinicName"
                    name="clinicName"
                    value={formData.clinicName}
                    onChange={handleChange}
                    disabled={isLoading}
                    required
                    className={`mt-1.5 ${errors.clinicName ? "border-red-500" : ""}`}
                  />
                  {errors.clinicName && (
                    <p className="text-red-500 text-xs mt-1">{errors.clinicName}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="clinicAddress">Clinic Address</Label>
                  <Input
                    id="clinicAddress"
                    name="clinicAddress"
                    value={formData.clinicAddress}
                    onChange={handleChange}
                    disabled={isLoading}
                    className="mt-1.5"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="latitude">Latitude</Label>
                    <Input
                      id="latitude"
                      name="latitude"
                      type="number"
                      step="any"
                      placeholder="e.g. 40.7128"
                      value={formData.latitude || ""}
                      onChange={(e) => setFormData(prev => ({ ...prev, latitude: e.target.value ? parseFloat(e.target.value) : undefined }))}
                      disabled={isLoading}
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="longitude">Longitude</Label>
                    <Input
                      id="longitude"
                      name="longitude"
                      type="number"
                      step="any"
                      placeholder="e.g. -74.0060"
                      value={formData.longitude || ""}
                      onChange={(e) => setFormData(prev => ({ ...prev, longitude: e.target.value ? parseFloat(e.target.value) : undefined }))}
                      disabled={isLoading}
                      className="mt-1.5"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      disabled={isLoading}
                      required
                      className={`mt-1.5 ${errors.city ? "border-red-500" : ""}`}
                    />
                    {errors.city && (
                      <p className="text-red-500 text-xs mt-1">{errors.city}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="state">State *</Label>
                    <Input
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      disabled={isLoading}
                      required
                      className={`mt-1.5 ${errors.state ? "border-red-500" : ""}`}
                    />
                    {errors.state && (
                      <p className="text-red-500 text-xs mt-1">{errors.state}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="zipCode">Zip Code</Label>
                    <Input
                      id="zipCode"
                      name="zipCode"
                      value={formData.zipCode}
                      onChange={handleChange}
                      disabled={isLoading}
                      className="mt-1.5"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="contactPhone">Contact Phone *</Label>
                  <Input
                    id="contactPhone"
                    name="contactPhone"
                    value={formData.contactPhone}
                    onChange={handleChange}
                    disabled={isLoading}
                    required
                    className={`mt-1.5 ${errors.contactPhone ? "border-red-500" : ""}`}
                  />
                  {errors.contactPhone && (
                    <p className="text-red-500 text-xs mt-1">{errors.contactPhone}</p>
                  )}
                </div>

                <Separator className="my-6" />

                <div>
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(val) => setFormData(prev => ({ ...prev, status: val as any }))}
                    disabled={isLoading}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ACTIVE">Active</SelectItem>
                      <SelectItem value="INACTIVE">Inactive</SelectItem>
                      <SelectItem value="SUSPENDED">Suspended</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Services Tab */}
          <TabsContent value="services" className="p-6 space-y-6 m-0 outline-none">
            <Card className="border-none shadow-none bg-transparent">
              <CardHeader className="px-0">
                <CardTitle className="text-xl flex items-center gap-2">
                  <FlaskConical className="h-5 w-5 text-primary" /> Test Configuration
                </CardTitle>
                <CardDescription>Define which tests are available at this clinic location.</CardDescription>
              </CardHeader>
              <CardContent className="px-0 space-y-6">
                {vendor && (
                  (isVariesByClinic(vendor.appointmentRequirement) ||
                    isVariesByClinic(vendor.cocRequirement)) && (
                    <div className="p-3 bg-slate-50 border border-slate-200 rounded space-y-3">
                      <p className="text-xs text-slate-600">
                        Clinic-specific requirements (shown because vendor settings vary by clinic).
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {isVariesByClinic(vendor.appointmentRequirement) && (
                          <div>
                            <Label>Appointment Requirement</Label>
                            <Select
                              value={formData.appointmentRequired ? "REQUIRED" : "WALK_INS"}
                              onValueChange={(value) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  appointmentRequired: value === "REQUIRED",
                                }))
                              }
                              disabled={isLoading}
                            >
                              <SelectTrigger className="mt-1.5">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="REQUIRED">Required</SelectItem>
                                <SelectItem value="WALK_INS">Not Required (Walk-ins)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}

                        {isVariesByClinic(vendor.cocRequirement) && (
                          <div>
                            <Label>COC Requirement</Label>
                            <Select
                              value={formData.cocRequired ? "REQUIRED" : "NOT_REQUIRED"}
                              onValueChange={(value) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  cocRequired: value === "REQUIRED",
                                }))
                              }
                              disabled={isLoading}
                            >
                              <SelectTrigger className="mt-1.5">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="REQUIRED">Required</SelectItem>
                                <SelectItem value="NOT_REQUIRED">Not Required</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                )}

                {vendor &&
                  (isVariesByClinic(vendor.appointmentRequirement) ||
                    isVariesByClinic(vendor.cocRequirement)) && (
                    <Separator className="my-6" />
                )}

                <div>
                  <Label>Available Tests *</Label>
                  <ClinicTestsMultiSelect
                    availableTests={availableTests}
                    selectedTestIds={formData.testTypeIds || []}
                    onChange={handleTestTypesChange}
                    disabled={isLoading || (!!vendor?.uniformTestConfiguration)}
                  />
                  {vendor?.uniformTestConfiguration && (
                    <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                      <p className="font-medium text-yellow-800 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" /> Uniform Test Configuration
                      </p>
                      <p className="text-yellow-700">Test selection is uniform for this vendor and cannot be changed.</p>
                    </div>
                  )}
                  {errors.testTypeIds && (
                    <p className="text-red-500 text-xs mt-1">{errors.testTypeIds}</p>
                  )}
                </div>

                <Separator className="my-6" />

                <div>
                  <Label>Drug Panels Offered</Label>
                  <DrugPanelMultiSelect
                    availablePanels={drugPanels}
                    selectedPanelIds={selectedDrugPanelIds}
                    onChange={handleDrugPanelsChange}
                    disabled={isLoading || (!!vendor?.uniformDrugConfiguration)}
                  />
                  {vendor?.uniformDrugConfiguration && (
                    <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                      <p className="font-medium text-yellow-800 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" /> Uniform Drug Configuration
                      </p>
                      <p className="text-yellow-700">Drug panels are uniform for this vendor and cannot be changed.</p>
                    </div>
                  )}
                </div>

                {!vendor?.uniformDrugConfiguration && selectedDrugPanelIds.length > 0 && (
                  <div>
                    <Label className="block mb-2">Drug Panel Aliases</Label>
                    <div className="space-y-3 p-4 bg-muted/20 rounded-lg border border-dashed border-primary/20">
                      <p className="text-sm text-muted-foreground">
                        Set custom names for drug panels as they appear to this clinic. Leave empty to use the default panel name.
                      </p>
                      <div className="space-y-2">
                        {selectedDrugPanelIds.map(panelId => {
                          const panel = drugPanels.find(p => p.id === panelId);
                          return (
                            <div key={panelId} className="flex items-center gap-3 p-2 bg-background rounded border">
                              <div className="flex-1">
                                <div className="text-sm font-medium">{panel?.panelName || `Panel #${panelId}`}</div>
                                <div className="text-xs text-muted-foreground">{panel?.numComponents || 0} components</div>
                              </div>
                              <div className="flex-1">
                                <Input
                                  placeholder={`Custom name (e.g., "DOT 5-Panel")`}
                                  value={panelAliases[panelId] || ""}
                                  onChange={(e) => handlePanelAliasChange(panelId, e.target.value)}
                                  disabled={isLoading}
                                  className="h-8 text-sm"
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pricing Tab */}
          <TabsContent value="pricing" className="p-6 space-y-6 m-0 outline-none">
            <Card className="border-none shadow-none bg-transparent">
              <CardHeader className="px-0">
                <CardTitle className="text-xl flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-primary" /> Location Pricing
                </CardTitle>
                <CardDescription>Configure pricing for tests and drug panels at this clinic location.</CardDescription>
              </CardHeader>
              <CardContent className="px-0 space-y-6">
                {vendor?.pricingModel === "LOCATION_SPECIFIC" && formData.testTypeIds && formData.testTypeIds.length > 0 && (
                  <div className="space-y-3">
                    <Label className="block font-semibold text-primary">Test Prices (Vendor Cost)</Label>
                    {errors.testPricesSummary && (
                      <p className="text-red-500 text-xs">{errors.testPricesSummary}</p>
                    )}
                    <div className="border rounded-xl overflow-hidden bg-background">
                      <table className="w-full text-sm">
                        <thead className="bg-muted">
                          <tr>
                            <th className="px-4 py-2 text-left font-medium">Test Name</th>
                            <th className="px-4 py-2 text-left w-40 font-medium">Cost ($)</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {formData.testTypeIds.map(testId => {
                            const test = availableTests.find(t => t.id === testId);
                            return (
                              <tr key={testId} className="hover:bg-muted/50 transition-colors">
                                <td className="px-4 py-2">{test?.testName || `Test #${testId}`}</td>
                                <td className="px-4 py-2">
                                  <Input
                                    type="number"
                                    placeholder="0.00"
                                    value={testPrices[testId] || ""}
                                    onChange={(e) => handleTestPriceChange(testId, e.target.value)}
                                    className={`h-8 ${errors[`testPrice-${testId}`] ? "border-red-500" : ""}`}
                                  />
                                  {errors[`testPrice-${testId}`] && (
                                    <p className="text-red-500 text-xs mt-1">{errors[`testPrice-${testId}`]}</p>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {!vendor?.uniformDrugConfiguration && selectedDrugPanelIds.length > 0 && (
                  <div className="space-y-3">
                    <Label className="block font-semibold text-primary">Drug Panel Prices (Vendor Cost)</Label>
                    {errors.drugPricesSummary && (
                      <p className="text-red-500 text-xs">{errors.drugPricesSummary}</p>
                    )}
                    <div className="border rounded-xl overflow-hidden bg-background">
                      <table className="w-full text-sm">
                        <thead className="bg-muted">
                          <tr>
                            <th className="px-4 py-2 text-left font-medium">Panel Name</th>
                            <th className="px-4 py-2 text-left w-40 font-medium">Cost ($)</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {selectedDrugPanelIds.map(id => {
                            const panel = drugPanels.find(p => p.id === id);
                            return (
                              <tr key={id} className="hover:bg-muted/50 transition-colors">
                                <td className="px-4 py-2">{panel?.panelName || `Panel #${id}`}</td>
                                <td className="px-4 py-2">
                                  <Input
                                    type="number"
                                    
                                    
                                    placeholder="0.00"
                                    value={drugPrices[id] || ""}
                                    onChange={(e) => handleDrugPriceChange(id, e.target.value)}
                                    className={`h-8 ${errors[`drugPrice-${id}`] ? "border-red-500" : ""}`}
                                  />
                                  {errors[`drugPrice-${id}`] && (
                                    <p className="text-red-500 text-xs mt-1">{errors[`drugPrice-${id}`]}</p>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {vendor?.pricingModel === "UNIFORM" && (
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded text-sm text-gray-600">
                    ℹ️ Pricing is uniform for this vendor and managed at the vendor level.
                  </div>
                )}

              </CardContent>
            </Card>
          </TabsContent>

          {/* Operations Tab */}
          <TabsContent value="operations" className="p-6 space-y-6 m-0 outline-none">
            <Card className="border-none shadow-none bg-transparent">
              <CardHeader className="px-0">
                <CardTitle className="text-xl flex items-center gap-2">
                  <Clock className="h-5 w-5 text-primary" /> Operating Hours
                </CardTitle>
                <CardDescription>Configure operating hours and collection fees for this clinic.</CardDescription>
              </CardHeader>
              <CardContent className="px-0 space-y-6">
                {vendor?.uniformOperatingHours ? (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm">
                    <p className="font-medium text-yellow-800 flex items-center gap-1 mb-2">
                      <AlertCircle className="h-4 w-4" /> Uniform Operating Hours
                    </p>
                    <p className="text-yellow-700 mb-2">Operating hours are uniform for this vendor and cannot be customized.</p>
                    <div className="mt-2 text-xs text-gray-600 space-y-1">
                      {Object.entries(operatingHours).map(([day, hours]) => (
                        <p key={day}><span className="capitalize font-medium">{day}:</span> {hours}</p>
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="setCustomHours"
                        checked={showOperatingHours}
                        onChange={(e) => setShowOperatingHours(e.target.checked)}
                        className="rounded border-gray-300"
                        disabled={isLoading}
                      />
                      <Label htmlFor="setCustomHours">Set Clinic-Specific Operating Hours</Label>
                    </div>

                    {showOperatingHours && (
                      <div className="grid grid-cols-2 gap-3 pl-6 border-l-2 border-blue-200">
                        {(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const).map((day) => (
                          <div key={day} className="flex items-center gap-2">
                            <Label className="w-12 capitalize text-sm font-medium">{day}:</Label>
                            <Input
                              type="text"
                              placeholder="09:00-17:00"
                              value={operatingHours[day] || ""}
                              onChange={(e) => handleOperatingHoursChange(day, e.target.value)}
                              disabled={isLoading}
                              className="flex-1 h-8 text-sm"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}

                <Separator className="my-6" />

                {vendor && (() => {
                  try {
                    const config = vendor.collectionFeeConfiguration 
                      ? (typeof vendor.collectionFeeConfiguration === 'string' 
                          ? JSON.parse(vendor.collectionFeeConfiguration) 
                          : vendor.collectionFeeConfiguration)
                      : null;
                      
                    if (config && config.type === "LOCATION_SPECIFIC") {
                      return (
                        <div className="space-y-4">
                           <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                              <div className="space-y-1">
                                <Label className="text-base font-semibold">Collection Fee Configuration</Label>
                                <p className="text-xs text-muted-foreground">Does this clinic apply collection fees for tests?</p>
                              </div>
                              <Select
                                value={hasCollectionFee ? "YES" : "NO"}
                                onValueChange={(val) => {
                                    setHasCollectionFee(val === "YES");
                                    if (val === "NO") {
                                        setFormData(prev => ({ ...prev, collectionFee: 0 }));
                                    }
                                }}
                              >
                                <SelectTrigger className="w-full sm:w-[180px]">
                                  <SelectValue placeholder="Has Fee?" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="YES">Yes (Applies Fees)</SelectItem>
                                  <SelectItem value="NO">No (Hide Fees)</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                          {hasCollectionFee && (
                            <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                              <Label htmlFor="collectionFee">Collection Fee ($)</Label>
                              <Input
                                id="collectionFee"
                                type="number"
                                name="collectionFee"
                                placeholder="25.00"
                                
                                
                                value={formData.collectionFee || ""}
                                onChange={handleChange}
                                disabled={isLoading}
                                className="mt-1.5"
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                This vendor allows location-specific collection fees.
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    } else if (config && config.type === "UNIFORM") {
                      return (
                        <div className="p-3 bg-gray-50 border border-gray-200 rounded text-sm text-gray-600">
                          ℹ️ Collection Fee is uniform for this vendor (${config.amount}).
                        </div>
                      );
                    } else if (config && config.type === "NONE") {
                        return (
                          <div className="p-3 bg-gray-50 border border-gray-200 rounded text-sm text-gray-600">
                            ℹ️ Collection Fees are disabled by the vendor.
                          </div>
                        );
                    }
                    return null;
                  } catch {
                    return null;
                  }
                })()}

                <Separator className="my-6" />

                {/* Result Delivery Section - Always shown */}
                <div className="space-y-3">
                  <Label className="block font-semibold text-primary">Result Delivery Override</Label>
                  <div className="space-y-2">
                    <Label htmlFor="resultDeliveryOverride">Delivery Method Override</Label>
                    <Select
                      value={formData.resultDeliveryOverride}
                      onValueChange={(value) => setFormData((prev) => ({ ...prev, resultDeliveryOverride: value }))}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Use vendor default" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="DEFAULT">Use vendor default</SelectItem>
                        <SelectItem value="EMAIL">Email</SelectItem>
                        <SelectItem value="FAX">Fax</SelectItem>
                        <SelectItem value="VENDOR_PORTAL">Vendor Portal</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Override the vendor's default result delivery method for this clinic.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Action Buttons */}
        <div className="sticky bottom-0 bg-background border-t p-4 flex gap-2 justify-end">
          <Button variant="outline" onClick={onClose} disabled={isLoading} type="button">
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? "Updating..." : "Update Clinic"}
          </Button>
        </div>
      </form>
    </div>
  );
}
